import { useApolloClient } from '@apollo/client';
import {
  Enum_Staff_Jobfunction,
  Enum_Staff_Role,
  MeQuery,
  notNull,
  OrgConfigurationValue,
  useLogoutMutation,
  useMeLazyQuery,
  UsersPermissionsMe,
} from '@cyren/common-lib';
import produce from 'immer';
import { first } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useAppConfig from '../app/use-app-config';
import { getAdminBasePath, isV2Pathname } from '../app/route-utils';
import { AppStateCookie } from '../app/use-app-state';
import { convStaff } from '../gql-tools/utils/convert';
import { useAdminParams } from '../survey/use-admin-params';
import { onError } from '../utils/apollo-utils';
import { AuthState } from './auth-state';
import { tokenManager } from '../v2/shared/utils/token-manager';
import { queryClient } from '../v2/api/query-client';
import AuthContainer from '../v2/shared/containers/AuthContainer';
import { customInstance } from '../v2/api/custom-instance';
import envs from '../common/envs';
import { setAccessToken as setGraphqlToken } from '../graphql/client';

export default function useAuth({
  navigateToAuthed,
  navigateToUnAuthed,
}: {
  navigateToAuthed?: string;
  navigateToUnAuthed?: string;
} = {}) {
  const loc = useLocation();
  const [sParams] = useSearchParams();
  const { orgIdParam } = useAdminParams();
  const adminBasePath = getAdminBasePath(loc.pathname);
  const isV2Path = isV2Pathname(loc.pathname);
  const isAuthPath = loc.pathname.includes('/auth/');

  const client = useApolloClient();
  const nav = useNavigate();

  const [appConfig, setAppConfig] = useAppConfig();
  const { authLoading, authInit } = appConfig || {};
  const [authState, setAuthState] = useRecoilState(AuthState);
  const { profiles, selectedProfile, user, jobFunctionOverride } = authState || {};
  const setAppStateCookie = useSetRecoilState(AppStateCookie);

  // AuthContainer for reactive login state
  const authContainer = AuthContainer.useContainer();

  const [logoutMutation] = useLogoutMutation({
    onError,
    onCompleted: () => {
      client.clearStore();
    },
  });

  const refreshSessionV2 = useCallback(async (): Promise<string | null> => {
    try {
      const bearerToken = tokenManager.getToken() || authState?.jwt || null;
      const response = await fetch(`${envs.v2BackendUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as { access_token?: string };
      return data?.access_token || null;
    } catch (error) {
      console.error('refreshSession error:', error);
      return null;
    }
  }, [authState?.jwt]);

  const fetchMeV2 = useCallback(async (): Promise<{
    user?: UsersPermissionsMe | null;
    staffs?: any[];
    orgConfigurationValues?: OrgConfigurationValue[];
  } | null> => {
    try {
      const data = await customInstance<{
        user?: UsersPermissionsMe | null;
        staffs?: any[];
        orgConfigurationValues?: OrgConfigurationValue[];
      }>({
        url: '/auth/me',
        method: 'GET',
      });
      return data || null;
    } catch (error) {
      console.error('fetchMe error:', error);
      return null;
    }
  }, []);

  // QA/QC FIX: [CRITICAL] - useEffect dependency array incomplete causing stale closures
  // LOGIC ISSUE: Missing 'nav' and 'sParams' in dependency array can cause navigation failures with stale references
  // SOLUTION: Include all referenced variables in dependency array or memoize with useCallback
  // IMPACT: Authentication redirects may fail or use stale navigation parameters

  /**
   * Navigate to user to somewhere when authed
   */
  useEffect(() => {
    if (navigateToAuthed && authInit && authState?.jwt) {
      nav(`${navigateToAuthed}${sParams ? `?${sParams}` : ''}`);
    }

    if (navigateToUnAuthed && authInit && !authState?.jwt) {
      nav(`${navigateToUnAuthed}${sParams ? `?${sParams}` : ''}`);
    }
  }, [authInit, authState?.jwt, navigateToAuthed, navigateToUnAuthed, nav, sParams]);

  const handleLoginV1 = useCallback(
    async (resp?: MeQuery['meV2']) => {
      const { jwt, user: nUser, staffs: rStaff, orgConfigurations } = resp || {};

      const token = jwt || authState?.jwt || null;
      tokenManager.setToken(token);
      setGraphqlToken(token);

      if (token) {
        authContainer.setIsLogin(true);
      }

      const nStaffs = rStaff?.data?.map(convStaff).filter(notNull);
      const urlSelectedProfile = nStaffs?.find(
        (st) => String(st.organization?.id) === String(orgIdParam),
      );
      const nProfile = urlSelectedProfile || first(nStaffs);

      setAuthState({
        jwt: token,
        authProvider: 'v1',
        user: nUser,
        profiles: nStaffs,
        selectedProfile: nProfile,
        orgConfigurationValues: (orgConfigurations as OrgConfigurationValue[]) || [],
      });

      if ([adminBasePath, `${adminBasePath}/`].includes(loc.pathname)) {
        nav(`${adminBasePath}/${nProfile?.organization?.id}`);
      }
    },
    [
      adminBasePath,
      authContainer,
      authState?.jwt,
      loc.pathname,
      nav,
      orgIdParam,
      setAuthState,
    ],
  );

  const handleLoginV2 = useCallback(
    async (accessToken?: string | null) => {
      const token = accessToken ?? authState?.jwt ?? null;

      if (!token) {
        return;
      }

      // V2 토큰 저장소 업데이트 (V1 경로에서는 Apollo 토큰도 동기화)
      tokenManager.setToken(token);
      if (!isV2Path) {
        setGraphqlToken(token);
      }

      // Set login state for reactive API calls
      authContainer.setIsLogin(true);

      const me = await fetchMeV2();

      if (!me?.user) {
        setAuthState(null);
        return;
      }

      const nStaffs = (me.staffs || []).filter(notNull);
      const urlSelectedProfile = nStaffs?.find(
        (st) => String(st.organization?.id) === String(orgIdParam),
      );
      const nProfile = urlSelectedProfile || first(nStaffs);

      setAuthState({
        jwt: token,
        authProvider: 'v2',
        user: me.user,
        profiles: nStaffs,
        selectedProfile: nProfile,
        orgConfigurationValues: me.orgConfigurationValues || [],
      });
      setAppConfig({ authInit: true, authLoading: false });

      if ([adminBasePath, `${adminBasePath}/`].includes(loc.pathname)) {
        nav(`${adminBasePath}/${nProfile?.organization?.id}`);
      }
    },
    [
      adminBasePath,
      authContainer,
      authState?.jwt,
      fetchMeV2,
      loc.pathname,
      nav,
      orgIdParam,
      setAppConfig,
      setAuthState,
    ],
  );

  const handleLogin = useCallback(
    async (
      payload?: MeQuery['meV2'] | { access_token?: string | null; jwt?: string | null } | string | null,
      options?: { forceV2?: boolean },
    ) => {
      const getTokenFromPayload = (
        value: typeof payload,
      ): string | null => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value !== 'object') return null;
        if ('access_token' in value && typeof value.access_token === 'string') {
          return value.access_token;
        }
        if ('jwt' in value && typeof value.jwt === 'string') {
          return value.jwt;
        }
        return null;
      };

      if (options?.forceV2 || isV2Path) {
        return handleLoginV2(getTokenFromPayload(payload));
      }

      return handleLoginV1(payload as MeQuery['meV2']);
    },
    [handleLoginV1, handleLoginV2, isV2Path],
  );

  const [fetchMeV1, { loading: gqlLoading }] = useMeLazyQuery({
    fetchPolicy: 'cache-first',
    // this line triggers refetching every time gql query contains UserPermissionsUser fragment.
    // fetchPolicy: 'cache-and-network',
    onCompleted: async (data) => {
      if (data?.meV2) {
        await handleLoginV1(data.meV2);
        setAppConfig({ authInit: true, authLoading: false });
      } else {
        setAppConfig({ authInit: true, authLoading: false });
        if (window.location.pathname.includes('/sp/')) {
          console.log('meV2 data is null, but /sp/ 경로이므로 admin 리다이렉트 생략');
          return;
        }
        console.log('meV2 data is null, redirecting to login');
        setAuthState(null);
        if (isV2Path) {
          nav(`${adminBasePath}/`);
        }
      }
    },
    onError: (error) => {
      console.error('meV2 query error:', error);
      // 토큰 갱신 실패 시 로그인 페이지로 리다이렉트
      setAppConfig({ authInit: true, authLoading: false });
      setAuthState(null);

      // 로그인 페이지로 리다이렉트
      if (isV2Path) {
        nav(`${adminBasePath}/`);
      }
    },
  });

  // QA/QC FIX: [CRITICAL] - useEffect dependency array missing critical dependencies
  // LOGIC ISSUE: Missing dependencies can cause stale closures and infinite loops. The effect depends on values that aren't in the dependency array.
  // SOLUTION: Add all referenced variables and functions to dependency array, or use useCallback for functions
  // IMPACT: Can cause authentication state inconsistencies, failed token refreshes, and memory leaks

  // QA/QC FIX: [CRITICAL] - Potential memory leak from unhandled promise rejections
  // LOGIC ISSUE: The catch block doesn't clean up pending requests if component unmounts during fetch
  // SOLUTION: Use AbortController for cancellation and cleanup in useEffect return function
  // IMPACT: Memory leaks and potential crashes from unhandled promise rejections after unmount

  // auth initialize
  useEffect(() => {
    if (!authLoading && !authInit) {
      setAppConfig({ authLoading: true, authInit: false });

      if (isAuthPath) {
        setAuthState(null);
        setAppConfig({ authInit: true, authLoading: false });
        return;
      }

      if (isV2Path) {
        (async () => {
          const token = await refreshSessionV2();
          if (token) {
            await handleLoginV2(token);
            setAppConfig({ authInit: true, authLoading: false });
            return;
          }

          // refresh failed: logout/redirect
          setAuthState(null);
          setAppConfig({ authInit: true, authLoading: false });

          if (window.location.pathname.includes('/sp/') || isAuthPath) {
            console.log('refresh failed, but /sp/ 경로이므로 admin 리다이렉트 생략');
            return;
          }
          nav(`${adminBasePath}/`);
        })().catch((error) => {
          console.error('auth init error:', error);
          setAuthState(null);
          setAppConfig({ authInit: true, authLoading: false });
          nav(`${adminBasePath}/`);
        });
        return;
      }

      (async () => {
        const token = await refreshSessionV2();
        if (token) {
          await handleLoginV2(token);
          setAppConfig({ authInit: true, authLoading: false });
          return;
        }

        await fetchMeV1({
          fetchPolicy: 'cache-and-network',
        });
      })().catch((error: unknown) => {
        console.error('fetchMe error:', error);
        setAppConfig({ authInit: true, authLoading: false });
        setAuthState(null);
      });
    }
  }, [
    adminBasePath,
    authInit,
    authLoading,
    fetchMeV1,
    handleLoginV2,
    isAuthPath,
    isV2Path,
    nav,
    refreshSessionV2,
    setAppConfig,
    setAuthState,
  ]);

  // V1 세션 상태로 /v2 경로에 진입한 경우 V2 세션으로 재검증한다.
  useEffect(() => {
    if (!isV2Path || isAuthPath || !authInit || authLoading) {
      return;
    }

    if (!authState?.jwt || authState?.authProvider !== 'v1') {
      return;
    }

    (async () => {
      const token = await refreshSessionV2();
      if (token) {
        await handleLoginV2(token);
        return;
      }

      // V2 refresh cookie가 없거나 유효하지 않으면 V2에서는 비로그인 상태로 처리.
      setAuthState(null);
    })().catch((error) => {
      console.error('v2 session bridge error:', error);
      setAuthState(null);
    });
  }, [
    authInit,
    authLoading,
    authState?.authProvider,
    authState?.jwt,
    handleLoginV2,
    isAuthPath,
    isV2Path,
    refreshSessionV2,
    setAuthState,
  ]);

  // Handle case where authInit is true but authState is null (token refresh failed)
  useEffect(() => {
    if (authInit && !authLoading && !authState?.jwt) {
      // 만약 URL에 /sp/가 포함되어 있으면 admin 라우팅을 하지 않음
      if (window.location.pathname.includes('/sp/') || isAuthPath) {
        console.log('Auth initialized but no valid token, but /sp/ 경로이므로 admin 리다이렉트 생략');
      } else if (isV2Path) {
        console.log('Auth initialized but no valid token, redirecting to login');
        nav(`${adminBasePath}/`);
      }
    }
  }, [authInit, authLoading, authState?.jwt, nav, adminBasePath, isAuthPath, isV2Path]);

  const clearClientAuth = useCallback(() => {
    // V2 토큰 관리 통합: 모든 토큰 삭제
    tokenManager.clearToken();
    setGraphqlToken(null);

    // Clear login state
    authContainer.setIsLogin(false);

    // Apollo 캐시 삭제
    client.clearStore();

    // React Query 캐시 삭제
    queryClient.clear();

    // QA/QC FIX: [HIGH] - Aggressive storage clearing may affect other applications
    // LOGIC ISSUE: localStorage.clear() and sessionStorage.clear() remove ALL data, not just app-specific data, potentially breaking other apps on same domain
    // SOLUTION: Only clear application-specific keys using a namespace prefix like 'cyren_':
    // Object.keys(localStorage).forEach(key => { if (key.startsWith('cyren_')) localStorage.removeItem(key); });
    // IMPACT: Can break functionality in other applications sharing the same domain
    // 네임스페이스 기반 localStorage 삭제 (개선)
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('cyren_') || key.startsWith('recoil')) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.clear();

    // QA/QC FIX: [MEDIUM] - Cookie clearing vulnerability and incomplete domain handling
    // LOGIC ISSUE: Cookie clearing attempts may fail for httpOnly cookies and doesn't handle subdomains properly
    // SOLUTION: Use secure cookie clearing with proper domain and path specifications:
    // - Add secure flag for HTTPS: document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;samesite=strict`
    // - Handle subdomains: document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${getRootDomain()}`
    // IMPACT: Incomplete logout may leave authentication tokens accessible, creating security vulnerabilities
    // 쿠키 클리어 (도메인별로)
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    });

    setAppConfig({ authLoading: false, authInit: false });
    setAuthState(null);

    setAppStateCookie((st) =>
      produce(st, (dr) => {
        dr.msAuth = false;
      }),
    );
  }, [authContainer, client, setAppConfig, setAppStateCookie, setAuthState]);

  const handleLogout = useCallback(async () => {
    if (isV2Path) {
      try {
        await fetch(`${envs.v2BackendUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        console.error('logout error:', error);
      }

      clearClientAuth();
      return;
    }

    await logoutMutation({
      onCompleted: () => {
        clearClientAuth();
      },
    });
  }, [clearClientAuth, isV2Path, logoutMutation]);

  const setSelectedProfile = (
    profileId: string,
    { basePath = adminBasePath }: { basePath?: string } = {}
  ) => {
    const nSelectedProfile = profiles?.find((p) => String(p.id) === String(profileId));
    const orgId = nSelectedProfile?.organization?.id;

    setAuthState({
      ...authState,
      selectedProfile: nSelectedProfile,
    });

    nav(`${basePath}/${orgId}`);
  };

  const setJobFunctionOverride = (
    newJobFunctionOverride: Enum_Staff_Jobfunction
  ) => {
    setAuthState({
      ...authState,
      jobFunctionOverride: newJobFunctionOverride,
    });
  };


  const getOrgConfigurationValue = (key: string) => {
    return authState?.orgConfigurationValues?.filter(config => config.orgId === selectedProfile?.organization?.id)
    .find(config => config.key === key);
  }

  return [
    {
      isLoggedIn: !!authState?.jwt && (!isV2Path || authState?.authProvider === 'v2'),
      authInit,
      authLoading,
      selectedProfile,
      selectedOrg: selectedProfile?.organization,
      orgId: selectedProfile?.organization?.id,
      isAdmin: selectedProfile?.role === Enum_Staff_Role.Admin,
      isSuperAdmin: user?.superAdmin === true,
      jobFunctionOverride,
      jobFunction: jobFunctionOverride || selectedProfile?.jobFunction,
      userId: user?.id,
      profiles,
      loading: authLoading || !authInit || gqlLoading,
      ...authState,
      auth: authState?.user,
      authState,
    },
    {
      setSelectedProfile,
      setJobFunctionOverride,
      handleLogin,
      handleLogout,
      navigate: nav,
      nav,
      setAuthState,
      setAppConfig,
      getOrgConfigurationValue,
    },
  ] as const;
}
